/**
 * QBridge Setup Wizard V2 - Streamlined Auto-Detection Flow
 *
 * Features (v1.9.0+):
 * - Single compact modal with inline terms acceptance
 * - Smart auto-detection of QBridge via /install-info
 * - Auto-start via qbridge:// protocol if not running
 * - Wizard-style Next → Next → Finish flow
 * - Terms checkbox prominently placed (not hidden at bottom)
 * - Institutional grade UI/UX
 *
 * Flow:
 * 1. Welcome + Terms (inline checkbox - prominent)
 * 2. Auto-detect OR Download + Install
 * 3. Connect to MT5
 * 4. Import Trades → Finish
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Checkbox,
    FormControlLabel,
    Link,
    CircularProgress,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Alert,
    Fade,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';

// Services
import {
    getInstallInfo,
    tryStartQBridge,
    smartConnectQBridge,
    authorizeQBridge,
    importTrades
} from '../../services/qbridge.service';
import { QBRIDGE_DOWNLOAD_URL, QBRIDGE_VERSION } from '../../config';

// ═══════════════════════════════════════════════════════════════════════════════
// THEME & STYLES
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
    background: '#0F0F14',
    surface: 'rgba(255, 255, 255, 0.03)',
    surfaceHover: 'rgba(255, 255, 255, 0.05)',
    border: 'rgba(255, 255, 255, 0.12)',
    borderLight: 'rgba(255, 255, 255, 0.08)',
    text: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.8)',
    textMuted: 'rgba(255, 255, 255, 0.6)',
    textDim: 'rgba(255, 255, 255, 0.4)',
    primary: '#27B56D',
    primaryLight: 'rgba(39, 181, 109, 0.15)',
    primaryBorder: 'rgba(39, 181, 109, 0.3)',
    primaryGlow: 'rgba(39, 181, 109, 0.4)',
    accent: '#7c3aed',
    info: '#3B82F6',
    warning: '#FFA500',
    warningBg: 'rgba(255, 165, 0, 0.1)',
    error: '#EF4444',
    success: '#22C55E'
};

const STEP_LABELS = ['Welcome', 'Setup', 'Connect', 'Import'];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const QBridgeSetupWizardV2 = ({ open = false, onClose, onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [wizardState, setWizardState] = useState({
        qbridgeRunning: false,
        qbridgeVersion: null,
        autoStarted: false,
        mt5Connected: false,
        mt5Account: null,
        mt5Broker: null,
        authorized: false,
        importComplete: false,
        importedCount: 0
    });
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [error, setError] = useState(null);

    // Get JWT token
    const getToken = useCallback(() => {
        return localStorage.getItem('qai_token') || localStorage.getItem('token');
    }, []);

    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 0: Welcome + Terms
    // ═══════════════════════════════════════════════════════════════════════════

    const WelcomeStep = () => (
        <Box sx={{ py: 2 }}>
            {/* Logo */}
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '16px',
                    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.success} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: `0 0 30px ${COLORS.primaryGlow}`
                }}
            >
                <Typography sx={{ color: '#FFFFFF', fontSize: '2rem', fontWeight: 800 }}>
                    Q
                </Typography>
            </Box>

            {/* Title */}
            <Typography
                sx={{
                    color: COLORS.text,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1
                }}
            >
                Connect Your MT5
            </Typography>

            <Typography
                sx={{
                    color: COLORS.textMuted,
                    fontSize: '0.9375rem',
                    textAlign: 'center',
                    mb: 3,
                    lineHeight: 1.6
                }}
            >
                Unlock AI-powered insights from your trading history
            </Typography>

            {/* Benefits */}
            <Box
                sx={{
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '12px',
                    p: 2.5,
                    mb: 3
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[
                        { icon: <SyncIcon />, text: 'Auto-sync trades from MT5' },
                        { icon: <RocketLaunchIcon />, text: 'Personalized AI coaching' },
                        { icon: <SecurityIcon />, text: 'Bank-grade security' }
                    ].map((item, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '8px',
                                    background: COLORS.primaryLight,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: COLORS.primary,
                                    fontSize: '1.125rem'
                                }}
                            >
                                {item.icon}
                            </Box>
                            <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.9375rem' }}>
                                {item.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* IMPORTANT: Terms acceptance - PROMINENTLY PLACED */}
            <Box
                sx={{
                    background: termsAccepted ? COLORS.primaryLight : COLORS.warningBg,
                    border: `1.5px solid ${termsAccepted ? COLORS.primaryBorder : COLORS.warning}`,
                    borderRadius: '12px',
                    p: 2.5,
                    mb: 3,
                    transition: 'all 0.3s ease'
                }}
            >
                {/* Legal Notice */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <WarningAmberIcon sx={{ color: COLORS.warning, fontSize: '1.25rem' }} />
                    <Typography sx={{ color: COLORS.warning, fontSize: '0.875rem', fontWeight: 600 }}>
                        EDUCATIONAL TOOL ONLY
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        color: COLORS.textSecondary,
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        mb: 2
                    }}
                >
                    Q.AI Coach is NOT a broker, signal provider, or investment advisor.
                    Trading carries HIGH RISK. Use for educational purposes only.
                </Typography>

                {/* Checkbox - LARGE AND PROMINENT */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            sx={{
                                color: COLORS.textMuted,
                                '&.Mui-checked': { color: COLORS.primary },
                                '& .MuiSvgIcon-root': { fontSize: '1.75rem' }
                            }}
                        />
                    }
                    label={
                        <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.9375rem', fontWeight: 500 }}>
                            I accept the{' '}
                            <Link
                                href="https://www.qforgex.com/terms"
                                target="_blank"
                                sx={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Terms of Service
                            </Link>
                            {' '}and{' '}
                            <Link
                                href="https://www.qforgex.com/privacy"
                                target="_blank"
                                sx={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Privacy Policy
                            </Link>
                        </Typography>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                />
            </Box>

            {/* Next Button */}
            <Button
                fullWidth
                onClick={() => setCurrentStep(1)}
                disabled={!termsAccepted}
                endIcon={<ArrowForwardIcon />}
                sx={{
                    py: 1.75,
                    borderRadius: '10px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: termsAccepted
                        ? `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.success} 100%)`
                        : COLORS.surface,
                    color: termsAccepted ? '#FFFFFF' : COLORS.textDim,
                    border: termsAccepted ? 'none' : `1px solid ${COLORS.border}`,
                    boxShadow: termsAccepted ? `0 4px 20px ${COLORS.primaryGlow}` : 'none',
                    '&:hover': {
                        boxShadow: termsAccepted ? `0 6px 24px ${COLORS.primaryGlow}` : 'none'
                    },
                    '&.Mui-disabled': {
                        background: COLORS.surface,
                        color: COLORS.textDim
                    }
                }}
            >
                Get Started
            </Button>

            {!termsAccepted && (
                <Typography
                    sx={{
                        color: COLORS.textDim,
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        mt: 1.5
                    }}
                >
                    Please accept the terms to continue
                </Typography>
            )}
        </Box>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 1: Auto-Detect OR Download
    // ═══════════════════════════════════════════════════════════════════════════

    const SetupStep = () => {
        const [detecting, setDetecting] = useState(false);
        const [downloadStarted, setDownloadStarted] = useState(false);

        useEffect(() => {
            // Auto-detect on mount
            handleAutoDetect();
        }, []);

        const handleAutoDetect = async () => {
            setDetecting(true);
            setLoadingMessage('Checking for QBridge...');
            setError(null);

            const token = getToken();
            if (!token) {
                setError('Please sign in to continue');
                setDetecting(false);
                return;
            }

            // Try smart connection (auto-detect + auto-start)
            const result = await smartConnectQBridge(token);

            if (result.connected) {
                // Success! Update state and advance
                setWizardState(prev => ({
                    ...prev,
                    qbridgeRunning: true,
                    qbridgeVersion: result.installInfo?.qbridge?.version || null,
                    autoStarted: result.autoStarted,
                    mt5Connected: result.installInfo?.mt5?.connected || false,
                    mt5Account: result.installInfo?.mt5?.account || null,
                    mt5Broker: result.installInfo?.mt5?.broker || null,
                    authorized: true
                }));

                setDetecting(false);

                // Auto-advance to next step
                setTimeout(() => {
                    setCurrentStep(2);
                }, 1500);
            } else {
                // Need manual download
                setDetecting(false);
                setError('QBridge not found. Please download and install.');
            }
        };

        const handleDownload = () => {
            window.open(QBRIDGE_DOWNLOAD_URL, '_blank');
            setDownloadStarted(true);
        };

        if (detecting) {
            return (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <CircularProgress size={48} sx={{ color: COLORS.primary, mb: 2 }} />
                    <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.9375rem', mb: 1 }}>
                        {loadingMessage}
                    </Typography>
                    {wizardState.autoStarted && (
                        <Typography sx={{ color: COLORS.primary, fontSize: '0.875rem' }}>
                            Starting QBridge...
                        </Typography>
                    )}
                </Box>
            );
        }

        if (wizardState.qbridgeRunning) {
            return (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: '3rem', color: COLORS.success, mb: 2 }} />
                    <Typography sx={{ color: COLORS.text, fontSize: '1.125rem', fontWeight: 600, mb: 0.5 }}>
                        QBridge Connected!
                    </Typography>
                    {wizardState.autoStarted && (
                        <Chip
                            label="Auto-started"
                            size="small"
                            sx={{ background: COLORS.primaryLight, color: COLORS.primary, mb: 2 }}
                        />
                    )}
                    <Typography sx={{ color: COLORS.textMuted, fontSize: '0.875rem' }}>
                        Version {wizardState.qbridgeVersion || QBRIDGE_VERSION}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ py: 2 }}>
                <Typography sx={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, mb: 2 }}>
                    Download QBridge
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Download Card */}
                <Box
                    sx={{
                        background: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        p: 3,
                        textAlign: 'center',
                        mb: 2
                    }}
                >
                    <DownloadIcon sx={{ fontSize: '2.5rem', color: COLORS.accent, mb: 1.5 }} />
                    <Typography sx={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, mb: 1 }}>
                        QBridge v{QBRIDGE_VERSION}
                    </Typography>

                    <Button
                        fullWidth
                        onClick={handleDownload}
                        startIcon={<DownloadIcon />}
                        sx={{
                            background: `linear-gradient(135deg, ${COLORS.accent} 0%, #a855f7 100%)`,
                            color: '#FFFFFF',
                            py: 1.5,
                            borderRadius: '10px',
                            fontWeight: 600,
                            textTransform: 'none',
                            mb: downloadStarted ? 2 : 0
                        }}
                    >
                        Download for Windows
                    </Button>

                    {downloadStarted && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            Download started! Install and click "Try Again"
                        </Alert>
                    )}
                </Box>

                {/* Retry Button */}
                <Button
                    fullWidth
                    onClick={handleAutoDetect}
                    startIcon={<RefreshIcon />}
                    sx={{
                        color: COLORS.textSecondary,
                        textTransform: 'none',
                        fontWeight: 500
                    }}
                >
                    Try Again
                </Button>
            </Box>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 2: Connect MT5
    // ═══════════════════════════════════════════════════════════════════════════

    const ConnectStep = () => {
        const [polling, setPolling] = useState(true);

        useEffect(() => {
            let interval;
            if (polling) {
                interval = setInterval(async () => {
                    const info = await getInstallInfo();
                    if (info.mt5?.connected) {
                        setWizardState(prev => ({
                            ...prev,
                            mt5Connected: true,
                            mt5Account: info.mt5.account,
                            mt5Broker: info.mt5.broker
                        }));
                        setPolling(false);
                    }
                }, 3000);
            }
            return () => clearInterval(interval);
        }, [polling]);

        if (wizardState.mt5Connected) {
            return (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: '3rem', color: COLORS.success, mb: 2 }} />
                    <Typography sx={{ color: COLORS.text, fontSize: '1.125rem', fontWeight: 600, mb: 0.5 }}>
                        MT5 Connected!
                    </Typography>
                    <Typography sx={{ color: COLORS.primary, fontSize: '0.9375rem', fontWeight: 600 }}>
                        {wizardState.mt5Account}
                    </Typography>
                    <Typography sx={{ color: COLORS.textMuted, fontSize: '0.875rem', mb: 3 }}>
                        {wizardState.mt5Broker}
                    </Typography>

                    <Button
                        fullWidth
                        onClick={() => setCurrentStep(3)}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.success} 100%)`,
                            color: '#FFFFFF',
                            py: 1.5,
                            borderRadius: '10px',
                            fontWeight: 600,
                            textTransform: 'none'
                        }}
                    >
                        Continue to Import
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ py: 3, textAlign: 'center' }}>
                <CircularProgress size={48} sx={{ color: COLORS.primary, mb: 2 }} />
                <Typography sx={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, mb: 1 }}>
                    Waiting for MT5...
                </Typography>
                <Typography sx={{ color: COLORS.textMuted, fontSize: '0.875rem', lineHeight: 1.6 }}>
                    Please open MetaTrader 5 and log in to your account
                </Typography>
            </Box>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 3: Import Trades
    // ═══════════════════════════════════════════════════════════════════════════

    const ImportStep = () => {
        const [importing, setImporting] = useState(false);

        const handleImport = async () => {
            setImporting(true);
            setLoadingMessage('Importing your trades...');

            const result = await importTrades();

            if (result.success) {
                setWizardState(prev => ({
                    ...prev,
                    importComplete: true,
                    importedCount: result.imported || 0
                }));
            } else {
                setError(result.error || 'Import failed');
            }

            setImporting(false);
        };

        if (wizardState.importComplete) {
            return (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: '4rem', color: COLORS.success, mb: 2 }} />
                    <Typography sx={{ color: COLORS.text, fontSize: '1.25rem', fontWeight: 700, mb: 0.5 }}>
                        All Set!
                    </Typography>
                    <Typography sx={{ color: COLORS.textMuted, fontSize: '0.9375rem', mb: 3 }}>
                        Imported {wizardState.importedCount} trades successfully
                    </Typography>

                    <Button
                        fullWidth
                        onClick={() => {
                            onComplete?.(wizardState.importedCount);
                            onClose?.();
                        }}
                        sx={{
                            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.success} 100%)`,
                            color: '#FFFFFF',
                            py: 1.75,
                            borderRadius: '10px',
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none'
                        }}
                    >
                        Finish
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ py: 2 }}>
                <Typography sx={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, mb: 2 }}>
                    Import Your Trades
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Button
                    fullWidth
                    onClick={handleImport}
                    disabled={importing}
                    startIcon={importing ? <CircularProgress size={20} /> : <SyncIcon />}
                    sx={{
                        background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.success} 100%)`,
                        color: '#FFFFFF',
                        py: 1.75,
                        borderRadius: '10px',
                        fontWeight: 600,
                        textTransform: 'none'
                    }}
                >
                    {importing ? 'Importing...' : 'Start Import'}
                </Button>

                {importing && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress sx={{ borderRadius: '4px' }} />
                        <Typography sx={{ color: COLORS.textMuted, fontSize: '0.875rem', textAlign: 'center', mt: 1 }}>
                            {loadingMessage}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    background: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '16px',
                    maxWidth: '480px'
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    pt: 2.5,
                    pb: 1.5
                }}
            >
                <Typography sx={{ color: COLORS.text, fontSize: '1.125rem', fontWeight: 700 }}>
                    QBridge Setup
                </Typography>
                <IconButton onClick={onClose} sx={{ color: COLORS.textMuted }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Progress Stepper */}
            <Box sx={{ px: 3, pb: 2 }}>
                <Stepper activeStep={currentStep} alternativeLabel>
                    {STEP_LABELS.map((label, index) => (
                        <Step key={label} completed={index < currentStep}>
                            <StepLabel
                                sx={{
                                    '& .MuiStepLabel-label': {
                                        color: index === currentStep ? COLORS.text : COLORS.textDim,
                                        fontSize: '0.75rem',
                                        fontWeight: index === currentStep ? 600 : 400
                                    }
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            {/* Content */}
            <DialogContent sx={{ px: 3, pb: 3 }}>
                <Fade in key={currentStep}>
                    <Box>
                        {currentStep === 0 && <WelcomeStep />}
                        {currentStep === 1 && <SetupStep />}
                        {currentStep === 2 && <ConnectStep />}
                        {currentStep === 3 && <ImportStep />}
                    </Box>
                </Fade>
            </DialogContent>
        </Dialog>
    );
};

export default QBridgeSetupWizardV2;
